import { useState } from 'react';
import { useMessageContext } from 'context/MessageContext';
import { v4 as uuidv4 } from 'uuid';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { ESearchParams, EStorageKey } from 'common/const';
import { useStorage } from 'common/hooks/useStorage';
import { useSearchParamsHook } from 'common/hooks/useSearchParamsHook';
import { IOrganizationData } from 'common/types/organization.types';
import { ITransactionData } from 'common/types/transaction.types';
import { IComplainSubmitPayload } from 'common/types/complain.types';
import { IEventPayload } from 'common/types/event.types';
import { IPaymentFormData, IPaymentStart, ITipsSubmitPayload } from 'common/types/tips.types';
import { ISavedPayerContact } from 'common/types/payerContact.types';
import { getAppSettings } from 'common/settings';
import { parseSavedPayerContact } from 'common/helpers/payerContact.helper';
import { parsePaymentId } from 'common/helpers/metrika.helper';
import { COMPLAIN_FORM_SUCCESS_MESSAGE_DURATION } from 'common/config';

const contentType = 'application/json;charset=utf-8';

export const useService = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const { t } = useTranslation();
  const { showSuccessMessage, showErrorMessage } = useMessageContext();
  const { getStorageItem, setStorageItem } = useStorage();
  const { getSearchParam, setParams } = useSearchParamsHook();

  const basePath = getAppSettings().apiBaseUrl;

  const postVisit = async (uid: string): Promise<void> => {
    try {
      const response = await fetch(`${basePath}/payment/visit`, {
        method: 'POST',
        headers: {
          'Content-Type': contentType,
        },
        body: JSON.stringify({ uid }),
      });

      if (!response.ok) {
        Sentry.captureException(response);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const setVisitorAndReviewParams = (visitorUid: string, reviewSubscription?: boolean) => {
    const review = getSearchParam(ESearchParams.Review);

    setParams(
      (params) => {
        try {
          params.set(ESearchParams.VisitorUid, visitorUid);
        } catch (error) {
          console.warn(error);
          Sentry.captureException(error);
        }

        if (review === null && reviewSubscription) {
          params.set(ESearchParams.Review, 'true');
        }

        return params;
      },
      { replace: true },
    );
  };

  const Service = {
    registerVisit: async (reviewSubscription?: boolean) => {
      const clientId = getStorageItem(EStorageKey.ClientId);

      if (clientId) {
        setVisitorAndReviewParams(clientId, reviewSubscription);
        await postVisit(clientId);
      } else {
        // @ts-ignore
        if (typeof ym !== 'undefined') {
          // @ts-ignore
          await ym(88190180, 'getClientID', async function (clientID: string) {
            setStorageItem(EStorageKey.ClientId, clientID);
            setVisitorAndReviewParams(clientID, reviewSubscription);
            await postVisit(clientID);
          });
        } else {
          const uid = uuidv4();

          setStorageItem(EStorageKey.ClientId, uid);
          setVisitorAndReviewParams(uid, reviewSubscription);
          await postVisit(uid);
        }
      }
    },
    getUserById: async (id: string): Promise<IOrganizationData | null> => {
      setLoading(true);

      try {
        const response = await fetch(`${basePath}/payment/recipient_info/user/${id}`);

        if (!response.ok) {
          Sentry.captureException(response);
          throw new Error(`${response.status}: ${response.statusText}`);
        } else {
          const json = await response.json();
          return json.data;
        }
      } catch (error) {
        // @ts-ignore
        showErrorMessage(error.message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    getOrganizationById: async (id: string): Promise<IOrganizationData | null> => {
      setLoading(true);

      try {
        const response = await fetch(`${basePath}/payment/recipient_info/organization/${id}`);

        if (!response.ok) {
          Sentry.captureException(response);
          throw new Error(`${response.status}: ${response.statusText}`);
        } else {
          const json = await response.json();
          // @ts-ignore
          return json.data;
        }
      } catch (error) {
        // @ts-ignore
        showErrorMessage(error.message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    getTransactionById: async (id: string): Promise<ITransactionData | null> => {
      setLoading(true);

      try {
        const response = await fetch(`${basePath}/payment/transaction/${id}`);

        if (!response.ok) {
          Sentry.captureException(response);
          throw new Error(`${response.status}: ${response.statusText}`);
        } else {
          const json = await response.json();
          return json.data;
        }
      } catch (error) {
        // @ts-ignore
        showErrorMessage(error.message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    submitComplainForm: async (payload: IComplainSubmitPayload) => {
      const { onSuccess, ...rest } = payload;

      try {
        const response = await fetch(`${basePath}/payment/complain`, {
          method: 'POST',
          headers: {
            'Content-Type': contentType,
          },
          body: JSON.stringify({ ...rest }),
        });

        if (!response.ok) {
          Sentry.captureException(response);
          throw new Error(`${response.status}: ${response.statusText}`);
        } else {
          showSuccessMessage(t('payment.complain.successMessage'), COMPLAIN_FORM_SUCCESS_MESSAGE_DURATION);
          onSuccess();
        }
      } catch (error) {
        // @ts-ignore
        showErrorMessage(error.message);
      }
    },
    sendEvent: async (payload: IEventPayload) => {
      try {
        const response = await fetch(`${basePath}/event`, {
          method: 'POST',
          headers: {
            'Content-Type': contentType,
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          Sentry.captureException(response);
        }
      } catch (error) {
        console.log(error);
      }
    },

    /* Сохранённые контакты гостя из куки API (payer-form, R-3). Кука уходит только с credentials.
     *  Отказ гостю не показывается и оплате не мешает: форма работает без контактов (R-6). */
    getPayerContact: async (): Promise<ISavedPayerContact | null> => {
      try {
        const response = await fetch(`${basePath}/payment/payer-contact`, { credentials: 'include' });

        if (!response.ok) {
          Sentry.captureException(response);
          return null;
        }

        return parseSavedPayerContact(await response.json());
      } catch (error) {
        console.log(error);
        return null;
      }
    },

    /* Создание платежа: POST start (payment-method-rename, R-10). Ответ 200 - адрес и поля формы банка или
     *  PaySpot в data, страница отправляет их как есть (R-4), и номер платежа рядом с data для воронки
     *  (metrika-landing, R-4); ответ без номера - форма как обычно, номер null. Любой отказ (не 2xx, сеть,
     *  пустое или чужое тело) гостю одно сообщение на языке страницы, текст причины из тела не показывается (R-11). */
    submitPaymentForm: async (payload: ITipsSubmitPayload): Promise<IPaymentStart | null> => {
      // Округление суммы — страновое, живёт у вызова (prepareAmountForSubmit, amount.helper.ts):
      // транспортный слой страну не знает (US-023)
      try {
        // credentials: успешный ответ ставит куку контактов гостя (payer-form, R-3)
        const response = await fetch(`${basePath}/payment/start`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': contentType,
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          Sentry.captureException(response);
          throw new Error(`${response.status}: ${response.statusText}`);
        }

        const json: { data?: IPaymentFormData | null; payment_id?: unknown } | null = await response.json();

        if (!json?.data?.Url) {
          throw new Error('start: no payment form in the response');
        }

        return { form: json.data, paymentId: parsePaymentId(json.payment_id) };
      } catch (error) {
        console.log(error);
        showErrorMessage(t('payment.tips.paymentStartFailed'));
        return null;
      } finally {
        setLoading(false);
      }
    },
  };

  return { Service, loading };
};
